import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Chip, Grid, TextField, Button,
  Select, MenuItem, InputLabel, FormControl, Dialog,
  DialogTitle, DialogContent, DialogActions, Tabs, Tab,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MonitorIcon from '@mui/icons-material/Monitor';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import HistoryIcon from '@mui/icons-material/History';
import Layout from '../../components/layout/Layout';
import { getStores } from '../../Redux/Actions/storeAction';
import { getScreens } from '../../Redux/Actions/screenAction';
import { addChangeRequest, getChangeRequests } from '../../Redux/Actions/changeRequestAction';
import { getPayments } from '../../Redux/Actions/paymentAction';
import './storeportal.css';

const REQUEST_TYPES = {
  price_change: '가격 변경',
  size_change: '사이즈 변경',
  menu_update: '메뉴 업데이트',
  other: '기타',
};

const STATUS_COLORS = {
  pending: { bg: '#fff3e0', color: '#e65100', label: '대기중' },
  in_progress: { bg: '#e3f2fd', color: '#1565c0', label: '처리중' },
  completed: { bg: '#e8f5e9', color: '#2e7d32', label: '완료' },
  rejected: { bg: '#fce4ec', color: '#c62828', label: '거절됨' },
};

const emptyRequestForm = {
  type: 'price_change',
  menuCode: '',
  menuName: '',
  changeDetails: '',
};

function storePortalUserId(user) {
  if (!user) return null;
  if (user.role === 'admin') return user._id;
  return user.user?._id || user._id;
}

function storePortalOpts(user) {
  if (!user) return {};
  if (user.role === 'admin') return {};
  return {
    role: user.role,
    subuserId: user._id,
    tljRegion: user.tljRegion,
  };
}

function StorePortal({ user }) {
  const dispatch = useDispatch();
  const { stores } = useSelector(s => s.Store);
  const { screens } = useSelector(s => s.Screen);
  const { requests } = useSelector(s => s.ChangeRequest);
  const { payments } = useSelector(s => s.Payment);

  const [requestForm, setRequestForm] = useState(emptyRequestForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [requestStoreId, setRequestStoreId] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const userId = storePortalUserId(user);
  const opts = storePortalOpts(user);

  useEffect(() => {
    if (userId) {
      dispatch(getStores(userId, opts));
      dispatch(getScreens(user?._id));
      dispatch(getChangeRequests(userId, { subuserId: opts.subuserId }));
    }
  }, [user?._id, userId, opts.role, opts.subuserId, opts.tljRegion]);

  useEffect(() => {
    if (userId && stores?.length > 0) {
      const ids = stores.map(s => s._id);
      dispatch(getPayments(userId, { storeIds: ids }));
    }
  }, [userId, stores?.length]);

  const myStores = stores || [];
  const myStoreIds = myStores.map(s => s._id);
  const selectedStores = myStores.filter(s => selectedStoreIds.includes(s._id));
  const singleStore = myStores.length === 1 ? myStores[0] : null;
  const myStore = singleStore || (selectedStoreIds[0] ? myStores.find(s => s._id === selectedStoreIds[0]) : myStores[0]);

  const screensForStore = (storeId) =>
    (screens || []).filter(s => String(s.store?._id || s.store) === String(storeId));
  const allScreens = myStoreIds.flatMap(sid => screensForStore(sid));

  const handleFormChange = (e) => {
    setRequestForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.changeDetails.trim()) return;
    const storeId = requestStoreId || myStore?._id;
    if (!storeId) return;
    setSubmitting(true);
    await dispatch(addChangeRequest({
      ...requestForm,
      user: userId,
      store: storeId,
      requestedBy: user?.role !== 'admin' ? user?._id : undefined,
    }));
    setRequestForm(emptyRequestForm);
    setDialogOpen(false);
    setRequestStoreId('');
    setSubmitting(false);
    dispatch(getChangeRequests(userId, { subuserId: opts.subuserId }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return typeof dateStr === 'string' ? dateStr.split('T')[0] : new Date(dateStr).toISOString().split('T')[0];
  };

  const titleByRole = {
    store_owner: 'My Store (점포)',
    store_manager: '담당 매장',
    tlj_manager: '담당 스토어 현황',
    tlj_admin: 'TLJ 현황',
  };
  const portalTitle = titleByRole[user?.role] || 'My Store Portal';

  return (
    <Layout title="My Store" user={user}>
      <Box sx={{ p: 2, maxWidth: 960, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <StorefrontIcon sx={{ color: '#551FFF', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#551FFF' }}>
            {portalTitle}
          </Typography>
        </Box>

        {myStores.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">
              배정된 점포가 없습니다. 관리자에게 문의하세요.
            </Typography>
          </Paper>
        ) : (
          <>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 2, borderColor: 'divider', mb: 2 }}>
              <Tab label="스토어 정보" />
              <Tab label="결제 정보" />
              <Tab label="변경 요청 / 히스토리" />
              <Tab label="현재 화면 (스크린)" />
            </Tabs>

            {/* 탭 1: 스토어 정보 */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {myStores.map(store => (
                  <Paper key={store._id} sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF', mb: 2 }}>
                      {store.tljNo || store.name || '점포'} {store.tljRegion && `(${store.tljRegion})`}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <div className="sp-info-row">
                          <span className="sp-info-label">TLJ No</span>
                          <span className="sp-info-value">{store.tljNo || '-'}</span>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="sp-info-row">
                          <span className="sp-info-label">매장 전화</span>
                          <span className="sp-info-value">{store.storePhone || '-'}</span>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="sp-info-row">
                          <span className="sp-info-label">주소</span>
                          <span className="sp-info-value">
                            {[store.address1, store.address2, store.town, store.state, store.zipcode].filter(Boolean).join(', ') || '-'}
                          </span>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Chip
                          label={store.status === 'active' ? '운영중' : '비활성'}
                          size="small"
                          sx={{
                            background: store.status === 'active' ? '#e8f5e9' : '#fce4ec',
                            color: store.status === 'active' ? '#2e7d32' : '#c62828',
                            fontWeight: 600, fontSize: 12,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <div className="sp-info-row">
                          <span className="sp-info-label">오픈일</span>
                          <span className="sp-info-value">{formatDate(store.openDate)}</span>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <div className="sp-info-row">
                          <span className="sp-info-label">계약 시작일</span>
                          <span className="sp-info-value">{formatDate(store.contractStartDate)}</span>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <div className="sp-info-row">
                          <span className="sp-info-label">결제 방식</span>
                          <span className="sp-info-value">{store.paymentOption === 'yearly' ? '연간' : '월간'}</span>
                        </div>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}

            {/* 탭 2: 결제 정보 */}
            {activeTab === 1 && (
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PaymentsIcon sx={{ color: '#551FFF', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF' }}>
                    결제 정보
                  </Typography>
                </Box>
                {(!payments || payments.length === 0) ? (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: 14 }}>
                    결제 이력이 없습니다.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {payments.map(p => (
                      <Box key={p._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {p.store?.tljNo || p.store?.name || '점포'} · {p.invoiceNumber || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(p.dueDate)} · {p.status} · ${Number(p.amount).toFixed(2)}
                          </Typography>
                        </Box>
                        <Chip
                          label={p.status}
                          size="small"
                          sx={{
                            background: p.status === 'paid' ? '#e8f5e9' : p.status === 'overdue' ? '#fce4ec' : '#fff3e0',
                            color: p.status === 'paid' ? '#2e7d32' : p.status === 'overdue' ? '#c62828' : '#e65100',
                            fontWeight: 600, fontSize: 11,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* 탭 3: 변경 요청 / 히스토리 */}
            {activeTab === 2 && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF' }}>
                    변경 요청 / 히스토리
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => { setRequestStoreId(myStore?._id || ''); setDialogOpen(true); }}
                    sx={{ background: '#551FFF', borderRadius: 2, fontSize: 13 }}
                  >
                    새 요청
                  </Button>
                </Box>
                {(!requests || requests.length === 0) ? (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: 14 }}>
                    제출된 요청이 없습니다.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {requests.map(req => {
                      const statusCfg = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
                      return (
                        <Box key={req._id} className="sp-request-item">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={REQUEST_TYPES[req.type] || req.type}
                              size="small"
                              sx={{ background: 'rgba(85,31,255,0.1)', color: '#551FFF', fontWeight: 600, fontSize: 11 }}
                            />
                            <Chip
                              label={statusCfg.label}
                              size="small"
                              sx={{ background: statusCfg.bg, color: statusCfg.color, fontWeight: 600, fontSize: 11 }}
                            />
                            {req.store?.tljNo && (
                              <Chip label={req.store.tljNo} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                              {req.createdAt ? new Date(req.createdAt).toLocaleDateString('ko-KR') : ''}
                            </Typography>
                          </Box>
                          {req.menuCode && (
                            <Typography variant="body2" sx={{ fontSize: 12, color: '#555' }}>
                              메뉴 코드: <strong>{req.menuCode}</strong>
                              {req.menuName ? ` · ${req.menuName}` : ''}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ fontSize: 13, mt: 0.5 }}>
                            {req.changeDetails}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            )}

            {/* 탭 4: 현재 화면 (스크린) */}
            {activeTab === 3 && (
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MonitorIcon sx={{ color: '#551FFF', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF' }}>
                    스크린 현황
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({allScreens.length}개)
                  </Typography>
                </Box>
                {allScreens.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: 14 }}>
                    연결된 스크린이 없습니다.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {myStores.map(store => (
                      <Grid item xs={12} key={store._id}>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                          {store.tljNo || store.name}
                        </Typography>
                        <Grid container spacing={2}>
                          {screensForStore(store._id).map(screen => (
                            <Grid item xs={12} sm={6} md={4} key={screen._id}>
                              <Box className={`sp-screen-card ${screen.status ? 'sp-screen-card--online' : 'sp-screen-card--offline'}`}>
                                <div className="sp-screen-code">CODE: {screen.code}</div>
                                <div className="sp-screen-name">{screen.name || '-'}</div>
                                <div className="sp-screen-playlist">
                                  {screen.playlist?.name || '플레이리스트 없음'}
                                </div>
                                <Chip
                                  label={screen.status ? 'Online' : 'Offline'}
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    background: screen.status ? '#e8f5e9' : '#fce4ec',
                                    color: screen.status ? '#2e7d32' : '#c62828',
                                    fontWeight: 600, fontSize: 11,
                                  }}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            )}
          </>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>변경 요청 제출</DialogTitle>
        <DialogContent>
          {myStores.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
              <InputLabel>대상 점포</InputLabel>
              <Select
                label="대상 점포"
                value={requestStoreId}
                onChange={e => setRequestStoreId(e.target.value)}
              >
                {myStores.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.tljNo || s.name || s._id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>요청 유형</InputLabel>
                <Select name="type" value={requestForm.type} label="요청 유형" onChange={handleFormChange}>
                  {Object.entries(REQUEST_TYPES).map(([val, label]) => (
                    <MenuItem key={val} value={val}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="메뉴 코드" name="menuCode" value={requestForm.menuCode} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="메뉴 이름" name="menuName" value={requestForm.menuName} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="변경 내용 *" name="changeDetails" value={requestForm.changeDetails}
                onChange={handleFormChange} multiline rows={4} placeholder="변경 사항을 자세히 설명해 주세요."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#888' }}>취소</Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={submitting || !requestForm.changeDetails.trim() || (!requestStoreId && myStores.length > 1)}
            sx={{ background: '#551FFF', borderRadius: 2 }}
          >
            {submitting ? '제출 중...' : '제출'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default StorePortal;
